<template>
  <h4 class="mb-2">{{label}}{{required ? '*' : ''}}</h4>
  <v-img v-if="mediaType !== 'video' && mediaType !== 'audio'"  color="grey-darken-4" :aspect-ratio="aspectRatio" width="100%" :src="mediaSrc" class="mb-3 rounded elevation-1"></v-img>
  <video v-if="mediaType === 'video'" controls :src="mediaSrc" style="width: 100%; aspect-ratio: 16/9" class="bg-grey-darken-4 mb-3"></video>
  <audio  v-if="mediaType === 'audio'" controls :src="mediaSrc" style="width: 100%;" class="mb-3"></audio>
  <v-autocomplete width="100%" max-width="350" :label="label + (required ? '*' : '')" :items="mediaOptions"
                  :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)"
                  :rules="required ? [rules.required] : []" :clearable="!required" append-icon="mdi-cloud-upload"
                  @click:append="addMediaDialog = true"></v-autocomplete>

  <v-dialog v-model="addMediaDialog" max-width="300" :persistent="mediaLoading">
    <v-card>
      <v-card-title>Carica {{mediaName}}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-text-field label="Titolo*" v-model="editingMedia.name" clearable ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-file-input label="Seleziona file" v-model="file" clearable :accept="mediaAccept" ></v-file-input>
          </v-col>
          <v-col cols="12">
            <p>oppure</p>
          </v-col>
          <v-col cols="12">
            <v-text-field label="Inserisci URL" v-model="editingMedia.url" clearable :disabled="!!file"></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="addMediaDialog = false;" :disabled="mediaLoading" >Annulla</v-btn>
        <v-btn @click="createMedia()" variant="elevated" color="primary" :disabled="mediaLoading || !validMediaForm" :loading="mediaLoading">Carica</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {computed, defineComponent, inject, type PropType, ref} from 'vue'
import type {Globals} from "@/plugins/globals.ts";
import rules from "@/utils/rules.ts";
import type {Media, MediaType} from "@/models.ts";
import api from "@/api";
import {type Messages, Msg} from "@/plugins/messages.ts";

export default defineComponent({
  name: "MediaField",
  props : {
    label : {
      type : String,
      required : true
    },
    modelValue : {
      type : String,
    },
    required : {
      type : Boolean,
      default : false,
    },
    mediaType : {
      type : String as PropType<MediaType>,
      default : 'icon'
    },
    aspectRatio : {
      type : Number,
      default : 16/9
    }
  },
  emits : ['update:modelValue'],
  setup(props, ctx) {
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const addMediaDialog = ref<boolean>(false)
    const mediaLoading = ref<boolean>(false)

    const editingMedia = ref<Media>({
      media: props.mediaType, name: "", url: ""
    })
    const file = ref<File|undefined>()

    return {
      addMediaDialog,
      mediaLoading,
      editingMedia,
      file,
      rules,
      mediaOptions : computed(()=>{
        return globals.media.value.filter(i => i.media === props.mediaType).map(i => {
          return { title : i.name, value : i._id }
        })
      }),
      mediaSrc : computed(()=>{
        const m = globals.media.value.find(i => i._id === props.modelValue)
        return m?.url
      }),
      mediaName : computed(()=>{
        switch (props.mediaType) {
          case "audio":
            return 'audio';
          case "video":
            return 'video';
          case "icon":
            return 'icona';
          case "cover":
            return 'immagine copertina';
          case "banner":
            return 'immagine banner';
          case "gallery":
            return 'immagine'
        }
      }),
      mediaAccept : computed(()=>{
        switch (props.mediaType) {
          case "audio":
            return '.mp3';
          case "video":
            return '.mp4';
          case "icon":
          case "cover":
          case "banner":
          case "gallery":
            return '.jpg, .jpeg, .png, .webp'
        }
      }),
      validMediaForm : computed(():boolean=>{
        return !!editingMedia.value.name && ( !!editingMedia.value.url || !!file.value )
      }),
      async createMedia() {
        mediaLoading.value = true
        editingMedia.value.media = props.mediaType
        const res = await api.post<Media>('media', editingMedia.value)
        if(!res.success) {
          mediaLoading.value = false
          messages.error(res.message ?? Msg.serverError)
          return
        }
        editingMedia.value = res.data!

        if(file.value) {
          let formData = new FormData();
          formData.append("media", file.value);
          const res = await api.upload<Media>( `media/${editingMedia.value._id}/upload`, formData )
          if(!res.success) {
            mediaLoading.value = false
            messages.error(res.message ?? Msg.serverError)
            await globals.refresh('media')
            return
          }
          editingMedia.value = res.data!
        }
        await globals.refresh('media')
        mediaLoading.value = false
        ctx.emit('update:modelValue', editingMedia.value._id)
        addMediaDialog.value = false;
      }
    }
  }
})
</script>

<style scoped>

</style>